import React, { useContext } from 'react';

import { IconButton } from '@material-ui/core';
import RefreshIcon from '@material-ui/icons/Refresh';
import { tasksFilterViewUpdateAction } from 'actions/tasksFilters';
import { useGoalsWeight } from 'components/GoalsWeight/GoalsWeightContext';
import { shallowEqual, useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { selectLoggedInEmployeeId, selectLoggedInEmployeeData } from 'selectors/loggedInEmployee';
import styled from 'styled-components';
import { FlexContainer, StyledIconButton, StyledTooltip } from 'styles/common';
import { checkUserIsGoalAdmin } from 'utils/helpers/rolesAccess';

import FilterIconSvg from 'assets/images/filter.svg?react';

import { MESH_LIST_TABS, TASK_TYPES_2 } from 'constants/ProjectConstants';
import { getColorFromKey } from 'constants/helper';
import { UpdateWeightageIcon, ListIcon } from 'constants/icons';
import { sendFilteredTabsOfHomepageEvent, sendRefreshedTabsOfHomepageEvent } from 'constants/mixpanelHelper';
import theme from 'constants/theme';

import { Typography3 } from 'uiToolkit/Typography';

import MeshListContext from './MeshListContext';
import CreateTask from './tasks/CreateTask';

const RightContainer = styled.div`
  display: flex;
  align-items: center;
`;

const LeftContainer = styled.div`
  display: flex;
  align-items: center;
  min-height: 32px;
`;

const Wrapper = styled.div`
  display: flex;
  justify-content: space-between;
  padding: 1rem;
  border-bottom: 1px solid ${(props) => props.theme.palette.divider};
`;

const StyledButtonIcon = styled(IconButton)`
  padding: 0;
  margin-left: 0.8rem;
`;

const StyledRefreshIcon = styled(RefreshIcon)`
  font-size: 15px;
`;

const CustomStyledIconButton = styled(StyledIconButton)`
  width: 24px;
  height: 24px;
  padding: 5px;
  border: 1px solid ${getColorFromKey('primary')};

  svg {
    fill: ${(props) => (props.disabled ? getColorFromKey('white') : getColorFromKey('primary'))};
  }

  &.MuiIconButton-root.Mui-disabled {
    background-color: ${getColorFromKey('primary')};
  }
`;

const StyledFilterIcon = styled(FilterIconSvg)`
  width: 10px;
  height: 10px;
`;

const IconWrapper = styled.div`
  display: flex;
  cursor: pointer;
  margin-left: 14px;
`;

const MeshListHeader = ({
  refreshHandler,
  employee,
  createNewHandler,
  createNewInProgress,
  filterPanelOpen,
  headerText = 'Mesh List',
  isMsteams = false,
  headerComponent = null,
}) => {
  const { dispatch, selectedTab, tasksFilters } = useContext(MeshListContext);
  const isCheckInSelected = selectedTab === MESH_LIST_TABS.checkIn.id;
  const isGoalSelected = selectedTab === MESH_LIST_TABS.goal.id;
  const isFeedbackSelected = selectedTab === MESH_LIST_TABS.feedback.id;
  const isReviewSelected = selectedTab === MESH_LIST_TABS.review.id;
  const isTaskSelected = selectedTab === MESH_LIST_TABS.task.id;
  const isLevelUpSelected = selectedTab === MESH_LIST_TABS.levelUp.id;
  const isCompetenciesSelected = selectedTab === MESH_LIST_TABS.competencies.id;
  const loggedInEmployeeId = useSelector(selectLoggedInEmployeeId);
  const loggedInEmployee = useSelector(selectLoggedInEmployeeData, shallowEqual);

  const history = useHistory();

  const getType = () => {
    if (isGoalSelected) {
      return TASK_TYPES_2.goal;
    }
    if (isFeedbackSelected) {
      return MESH_LIST_TABS.feedback.taskType;
    }
    return TASK_TYPES_2.task;
  };

  const getRefreshEventTypeKeyword = () => {
    let selectedTabMeta = {};
    Object.keys(MESH_LIST_TABS).forEach((key) => {
      if (MESH_LIST_TABS[key].id === selectedTab) {
        selectedTabMeta = MESH_LIST_TABS[key];
      }
    });
    const returnKey = selectedTabMeta?.displayName ?? '';
    if (returnKey === 'Tasks') {
      return 'Initiatives';
    }
    return returnKey;
  };

  // const onSuccess = ({ editedGoals, employeeId }) => {
  //   // if (primaryEmployee.id === employeeId) {
  //   //   const updatedWeightage = editedGoals.filter(goal => goal.goalId === taskDetails.id)[0]
  //   //     ?.weightage;
  //   // }
  //   hideSidePanel();
  // };

  // const onCancel = () => {
  //   hideSidePanel();
  // };

  const checkIfManager = () => {
    const loggedInEmployeeEmail = loggedInEmployee.email;
    const visitedProfileEmail = employee.email;
    const visitedProfileManagerEmail = employee.manager.email;

    return loggedInEmployeeEmail === visitedProfileManagerEmail || loggedInEmployeeEmail === visitedProfileEmail;
  };

  const RenderMyWeightageIcon = () => {
    const { showSidePanel, hideSidePanel } = useGoalsWeight();
    const employeeId = checkUserIsGoalAdmin() || checkIfManager() ? employee?.id : loggedInEmployeeId;

    const openMyWeightageSidePanel = () => {
      showSidePanel({
        employeeId,
        canEdit: true,
        onCancel: () => {
          hideSidePanel();
        },
        onSuccess: () => {
          hideSidePanel();
        },
      });
    };
    if (isGoalSelected && (checkUserIsGoalAdmin() || checkIfManager())) {
      return (
        <StyledTooltip title="Update Weights">
          <IconWrapper onClick={openMyWeightageSidePanel}>
            <UpdateWeightageIcon />
          </IconWrapper>
        </StyledTooltip>
      );
    }
    return null;
  };

  const showRefreshIcon = !isLevelUpSelected && !isCompetenciesSelected;
  const showCreateNewIconButton =
    !isCheckInSelected && !isReviewSelected && !isLevelUpSelected && !isCompetenciesSelected;

  if (headerComponent && typeof headerComponent === 'function' && isLevelUpSelected) {
    return headerComponent();
  }

  return (
    <Wrapper>
      <LeftContainer>
        <ListIcon size="tiny" color={theme.palette.icons.black2} />
        <Typography3 leftGutter={1.5} color="textPrimary">
          {headerText}
        </Typography3>
      </LeftContainer>

      <RightContainer>
        {showRefreshIcon ? (
          <StyledTooltip title="Refresh List">
            <StyledButtonIcon
              onClick={() => {
                sendRefreshedTabsOfHomepageEvent({
                  eventProperties: {
                    type: getRefreshEventTypeKeyword(),
                  },
                });
                refreshHandler();
              }}
            >
              <CustomStyledIconButton circular borderColor="primary">
                <StyledRefreshIcon />
              </CustomStyledIconButton>
            </StyledButtonIcon>
          </StyledTooltip>
        ) : null}

        {showCreateNewIconButton && (
          <>
            <CreateTask createHandler={createNewHandler} createNewInProgress={createNewInProgress} type={getType()} />

            {!isMsteams
              ? isTaskSelected && (
                  <FlexContainer
                    leftGutter={2}
                    onClick={() => {
                      !tasksFilters.isFilterViewOpen &&
                        sendFilteredTabsOfHomepageEvent({
                          eventProperties: { type: 'Initiatives' },
                        });
                      dispatch(tasksFilterViewUpdateAction(!tasksFilters.isFilterViewOpen));
                    }}
                  >
                    <CustomStyledIconButton circular disabled={tasksFilters.isFilterViewOpen} borderColor="primary">
                      <StyledFilterIcon />
                    </CustomStyledIconButton>
                  </FlexContainer>
                )
              : null}
          </>
        )}
        <RenderMyWeightageIcon />
      </RightContainer>
    </Wrapper>
  );
};

export { MeshListHeader };
